/**
 * What the recipe editor writes for « Préparation » and « Prêt en » (#850).
 *
 * A time is sent only when the cook changed it. Every save used to send the
 * total, so a title-only edit stamped `timeSource: 'person'` and froze the
 * model's time as the cook's: the timeline sweep leaves a person's time alone
 * and would never have run on that recipe again.
 *
 * A changed total also sets `readyInMinutesMin`, because a cook's answer has no
 * range. A blank or cleared field writes nothing: there is no number to store.
 */

export interface OpenedTimes {
  handsOnText: string;
  readyText: string;
}

export interface EditedTimes {
  handsOnMinutes?: number;
  readyInMinutes?: number;
  readyInMinutesMin?: number;
  timeSource?: 'person';
  timelineStatus?: 'person';
}

/** Whole minutes: a blank or zero is no value (0), anything else that is not digits is null. */
const minutesOf = (text: string): number | null => {
  const t = text.trim();
  if (t === '') return 0;
  return /^\d+$/.test(t) ? parseInt(t, 10) : null;
};

export type EditedTimesError = 'hands_on_exceeds_ready' | 'not_a_number';

export function editedTimes(
  handsOnText: string,
  readyText: string,
  opened: OpenedTimes = { handsOnText: '', readyText: '' },
): EditedTimes | { error: EditedTimesError } {
  const changed = (text: string, was: string) => text.trim() !== was.trim();
  const handsOnChanged = changed(handsOnText, opened.handsOnText);
  const readyChanged = changed(readyText, opened.readyText);
  if (!handsOnChanged && !readyChanged) return {};

  // A changed field counts as what it now says: a cleared one is no value, it
  // is not its opened number, and it is not checked against the other field.
  const handsOn = handsOnChanged ? minutesOf(handsOnText) : minutesOf(opened.handsOnText) ?? 0;
  const ready = readyChanged ? minutesOf(readyText) : minutesOf(opened.readyText) ?? 0;
  if (handsOn === null || ready === null) return { error: 'not_a_number' };
  if (handsOn > 0 && ready > 0 && handsOn > ready) return { error: 'hands_on_exceeds_ready' };

  const writesHandsOn = handsOnChanged && handsOn > 0;
  const writesReady = readyChanged && ready > 0;
  if (!writesHandsOn && !writesReady) return {};
  return {
    ...(writesHandsOn && { handsOnMinutes: handsOn }),
    ...(writesReady && { readyInMinutes: ready, readyInMinutesMin: ready }),
    timeSource: 'person',
    timelineStatus: 'person',
  };
}
